import React, { useMemo } from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import { BreathStep } from '../../data/apneaProtocols';

/**
 * Per-breath metronome for the guided breathing phases.
 *
 * The breathe-up used to be a bare countdown: the diver was told to breathe slowly
 * for two and a half minutes without being told the cadence. The manual's
 * ventilation pattern is specific — a relatively quick 2 s inhale (kept short to
 * avoid raising heart rate), a 2 s pause, then a long 10–15 s exhale — and that
 * ratio is the whole point of the phase, so it needs showing rather than implying.
 *
 * Timing is derived from a wall-clock offset, consistent with the session runner.
 * A backgrounded tab must not drift the cadence relative to the phase countdown.
 */

interface BreathingGuideProps {
  steps: BreathStep[];
  /** Seconds elapsed in the phase, from the timer hook. */
  elapsedSeconds: number;
  /** Suppress the animation for reduced-motion users. */
  reducedMotion?: boolean;
}

const ACTION_LABEL: Record<BreathStep['action'], string> = {
  inhale: 'Inhale',
  exhale: 'Exhale',
  hold: 'Hold',
  holdEmpty: 'Hold empty',
};

const BreathingGuide: React.FC<BreathingGuideProps> = ({
  steps,
  elapsedSeconds,
  reducedMotion = false,
}) => {
  const theme = useTheme();

  const cycleSeconds = useMemo(
    () => steps.reduce((sum, s) => sum + (s.seconds ?? 0), 0),
    [steps]
  );

  // Which step of the cycle we are in, and how far through it.
  const { step, stepRemaining, fraction } = useMemo(() => {
    if (cycleSeconds <= 0) {
      return { step: steps[0], stepRemaining: 0, fraction: 0 };
    }
    let offset = elapsedSeconds % cycleSeconds;
    for (const candidate of steps) {
      const length = candidate.seconds ?? 0;
      if (offset < length) {
        return {
          step: candidate,
          stepRemaining: Math.ceil(length - offset),
          fraction: length > 0 ? offset / length : 0,
        };
      }
      offset -= length;
    }
    const last = steps[steps.length - 1];
    return { step: last, stepRemaining: 0, fraction: 1 };
  }, [steps, elapsedSeconds, cycleSeconds]);

  // Expand on inhale, contract on exhale, hold steady on a pause — so the shape
  // itself carries the instruction and can be followed without reading.
  const scale = useMemo(() => {
    if (reducedMotion) return 1;
    switch (step?.action) {
      case 'inhale': return 0.6 + fraction * 0.4;
      case 'exhale': return 1 - fraction * 0.4;
      case 'hold': return 1;
      case 'holdEmpty': return 0.6;
      default: return 1;
    }
  }, [step, fraction, reducedMotion]);

  if (!step) return null;

  return (
    <Box sx={{ textAlign: 'center', py: 1 }}>
      <Box
        sx={{
          position: 'relative',
          width: 132,
          height: 132,
          mx: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: 132,
            height: 132,
            borderRadius: '50%',
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            transform: `scale(${scale})`,
            transition: reducedMotion ? 'none' : 'transform 0.25s linear',
          }}
        />
        <Box sx={{ position: 'relative' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
            {ACTION_LABEL[step.action]}
          </Typography>
          <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {stepRemaining}
          </Typography>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {step.cue}
      </Typography>
    </Box>
  );
};

export default BreathingGuide;
